import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'August 4, 2026';
const CONTACT_EMAIL = 'info@sanitylounge.com';

const LegalLayout = ({ title, testId, children }) => (
  <div data-testid={testId} className="min-h-screen bg-black pt-20">
    <section className="py-16">
      <div className="max-w-3xl mx-auto px-6 sm:px-12">
        <h1 className="font-outfit text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-sm text-zinc-500 mb-10">Effective date: {EFFECTIVE_DATE}</p>
        <div className="space-y-6 text-zinc-300 leading-relaxed text-[15px]">
          {children}
        </div>
        <div className="mt-14 pt-6 border-t border-zinc-800 text-sm text-zinc-500">
          Questions? Contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-500 hover:text-purple-400 underline">
            {CONTACT_EMAIL}
          </a>.
        </div>
      </div>
    </section>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-outfit text-2xl font-semibold text-white mt-10 mb-3">{children}</h2>
);

const H3 = ({ children }) => (
  <h3 className="font-outfit text-lg font-semibold text-white mt-6 mb-2">{children}</h3>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1">{children}</ul>
);

const A = ({ href, children }) => (
  <a href={href} className="text-purple-500 hover:text-purple-400 underline">{children}</a>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" testId="privacy-page">
    <p>
      This Privacy Policy describes how What's Happening Baltimore ("we", "us", or
      "our") collects, uses, and shares information when you use our services: the
      What's Happening Baltimore website (whatshappeningbaltimore.com), our events
      calendar (events.whatshappeningbaltimore.com), and the What's Happening
      Baltimore mobile applications for iOS and Android (together, the "Services").
      Our event listings and publisher accounts are powered by our events platform,
      What's Happening Events (whatshappeningevents.com), which is operated by us and
      covered by this policy where you interact with it through the Services.
    </p>

    <H2>Information We Collect</H2>

    <H3>Contact and newsletter forms</H3>
    <p>
      When you fill out our contact form or subscribe to updates, we collect the
      information you enter, such as your name, email address, and phone number.
      These forms are provided by LeadConnector (HighLevel), a customer relationship
      management service that stores this information on our behalf so we can respond
      to you and, if you subscribe, send you updates about Baltimore events. You can
      unsubscribe from marketing messages at any time using the link in any message
      or by contacting us.
    </p>

    <H3>Publisher and administrator accounts</H3>
    <p>
      Event publishers and site administrators sign in with an email address, first
      and last name, and a password. Passwords are stored only as salted bcrypt
      hashes — never in plain text. If a publisher signs in with Google on our events
      platform, we receive their Google account identifier, name, email address, and
      profile photo URL from Google instead of a password. The general public can
      browse events without creating an account.
    </p>

    <H3>Event submissions</H3>
    <p>
      When you submit an event for consideration, we collect the contact details you
      provide — your name, email address, optionally a phone number, and any
      organization name, website, or description you include — so our team can review
      the submission and contact you about it.
    </p>

    <H3>Content you provide</H3>
    <p>
      Events, images, and profile information submitted by publishers are stored by
      us. Published events (titles, descriptions, dates, venues, host names, images,
      and ticket links) are public and visible to anyone through our website,
      calendar, mobile apps, and API.
    </p>

    <H3>Automatically collected information</H3>
    <UL>
      <li>
        <strong>Website analytics.</strong> The website uses Google Analytics to
        understand how visitors use the site (pages viewed, approximate location,
        device and browser information). Google Analytics sets cookies and receives
        this data under Google's own privacy policy.
      </li>
      <li>
        <strong>Event analytics.</strong> When you view an event or click a ticket
        link on our calendar, we record the view or click together with your IP
        address, browser information (user agent), referrer, and a session
        identifier, so we can show organizers aggregate statistics about their
        events.
      </li>
      <li>
        <strong>Sign-in and security data.</strong> When publishers or administrators
        sign in, we record IP address, browser/device information, and session
        identifiers to secure accounts and detect suspicious activity.
      </li>
    </UL>

    <H3>Information the mobile apps collect</H3>
    <p>
      The What's Happening Baltimore mobile apps let anyone browse published events
      without an account, and let event publishers sign in to manage their events.
      The apps collect only the sign-in credentials a publisher enters (sent securely
      to our servers) and the standard request data described above. Sign-in tokens
      are stored in your device's secure storage (iOS Keychain / Android Keystore).
      The apps do <strong>not</strong> access your location, contacts, camera,
      photos, or microphone, and contain no advertising and no third-party analytics
      SDKs.
    </p>

    <H2>How We Use Information</H2>
    <UL>
      <li>To provide, operate, and maintain the Services;</li>
      <li>To respond to your inquiries and review event submissions;</li>
      <li>To send updates you have subscribed to;</li>
      <li>To authenticate publishers and administrators and keep accounts secure;</li>
      <li>To send transactional emails such as email verification, password resets, and submission notifications;</li>
      <li>To provide organizers with aggregate analytics about their events;</li>
      <li>To automatically suggest categories, hosts, and venues for submitted events;</li>
      <li>To understand usage of the Services and improve them; and</li>
      <li>To comply with legal obligations and enforce our Terms of Use.</li>
    </UL>
    <p>
      We do not sell your personal information, and we do not use it for third-party
      advertising.
    </p>

    <H2>How We Share Information</H2>
    <p>We share information only as needed to run the Services:</p>
    <UL>
      <li>
        <strong>Service providers.</strong> We use LeadConnector (HighLevel) to
        operate our contact and subscription forms and send updates; Google Analytics
        for website analytics; Twilio SendGrid to deliver transactional email (they
        receive your email address and name); Google Cloud Storage to host uploaded
        images; Google to provide optional Google Sign-In for publishers; PostHog for
        analytics on our events platform's publisher web app; and OpenAI's API to
        automatically categorize submitted events (the event's title and description
        are sent for matching — submitter contact details are not included in
        prompts).
      </li>
      <li>
        <strong>Our team and event organizers.</strong> If you submit an event, our
        administrators — and, where applicable, the organization operating the
        calendar you submitted to — can see your submission and the contact details
        you provided.
      </li>
      <li>
        <strong>Configured integrations.</strong> Publishers can configure webhooks
        and integrations (for example, website plugins, calendar feeds, or Slack
        notifications) that transmit event data to endpoints they choose.
      </li>
      <li>
        <strong>Legal.</strong> We may disclose information if required by law or to
        protect the rights, safety, or property of our users or the public.
      </li>
    </UL>

    <H2>Cookies and Local Storage</H2>
    <p>
      Google Analytics and LeadConnector set their own cookies as described above.
      Our web applications store sign-in tokens for publishers and administrators in
      the browser's local storage. We do not use advertising cookies.
    </p>

    <H2>Data Retention</H2>
    <p>
      We retain contact-form and subscription information until you ask us to delete
      it or unsubscribe. Account information is retained for as long as the account
      exists. Event content remains until it is deleted by a publisher or
      administrator. Sign-in, verification, and analytics records are retained to
      maintain security and provide statistics.
    </p>

    <H2>Your Rights and Choices</H2>
    <p>
      You may request a copy of your personal information, ask us to correct it, or
      ask us to delete it — including deleting a publisher account — by emailing{' '}
      <A href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</A> from the email address
      associated with the information. We will act on verified requests within 30
      days. Depending on where you live, you may have additional rights under local
      law (for example in the EEA, UK, or California), including the right to lodge a
      complaint with a supervisory authority; we honor such requests as required by
      the law that applies to you.
    </p>

    <H2>Security</H2>
    <p>
      All traffic to the Services is encrypted in transit using HTTPS. Passwords are
      hashed with bcrypt. Mobile sign-in tokens are kept in the operating system's
      secure storage. No method of transmission or storage is completely secure, but
      we work to protect your information using industry-standard practices.
    </p>

    <H2>Children</H2>
    <p>
      The Services are not directed to children under 13, and we do not knowingly
      collect personal information from children under 13. If you believe a child has
      provided us personal information, contact us and we will delete it.
    </p>

    <H2>Changes to This Policy</H2>
    <p>
      We may update this Privacy Policy from time to time. We will post the updated
      version on this page and update the effective date above. Material changes will
      be communicated through the Services or by email.
    </p>

    <H2>Contact Us</H2>
    <p>
      What's Happening Baltimore — Baltimore, Maryland —{' '}
      <A href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</A>
    </p>
  </LegalLayout>
);

export const TermsOfUse = () => (
  <LegalLayout title="Terms of Use" testId="terms-page">
    <p>
      These Terms of Use ("Terms") govern your use of the What's Happening Baltimore
      website (whatshappeningbaltimore.com), our events calendar
      (events.whatshappeningbaltimore.com), and the What's Happening Baltimore mobile
      applications for iOS and Android (together, the "Services"), operated by What's
      Happening Baltimore ("we", "us", or "our"). By using the Services, you agree to
      these Terms and to our{' '}
      <Link to="/privacy" className="text-purple-500 hover:text-purple-400 underline">
        Privacy Policy
      </Link>
      . If you do not agree, do not use the Services.
    </p>

    <H2>1. The Services</H2>
    <p>
      The Services let the public discover and browse Baltimore-area events, let
      anyone submit events for consideration, and let approved event publishers
      manage their events. Event listings are powered by our events platform, What's
      Happening Events.
    </p>

    <H2>2. Accounts</H2>
    <p>
      Publisher and administrator accounts are provisioned through our events
      platform. You are responsible for maintaining the confidentiality of your
      credentials and for all activity under your account. You must provide accurate
      information and be at least 13 years old to use the Services (or older where
      local law requires). You may not share credentials or impersonate another
      person or organization. Notify us promptly of any unauthorized use of your
      account.
    </p>

    <H2>3. Your Content</H2>
    <p>
      You retain ownership of the content you submit — events, descriptions, images,
      and profile information ("Content"). By submitting Content, you grant us a
      worldwide, non-exclusive, royalty-free license to host, store, reproduce,
      display, distribute, and adapt (for formatting purposes) that Content in order
      to operate and promote the Services, including displaying published events
      publicly through our website, calendar, embeds, feeds, the mobile apps, and our
      API, and distributing them through integrations that publishers configure. This
      license ends when the Content is deleted from the Services, except where it has
      already been distributed publicly or through integrations, or where retention
      is required by law.
    </p>
    <p>
      You represent that you have the rights necessary to submit your Content and
      that it does not infringe anyone's rights.
    </p>

    <H2>4. Acceptable Use</H2>
    <p>You agree not to:</p>
    <UL>
      <li>Post Content that is unlawful, deceptive, defamatory, obscene, harassing, hateful, or that infringes intellectual-property, privacy, or publicity rights;</li>
      <li>Submit false, misleading, or fraudulent events, or events you are not authorized to publicize;</li>
      <li>Attempt to gain unauthorized access to the Services, other accounts, or our systems, or probe or test their vulnerability without authorization;</li>
      <li>Interfere with the operation of the Services, including by introducing malware, scraping at disruptive volumes, circumventing rate limits, or overloading our infrastructure;</li>
      <li>Use the Services to send spam or unsolicited communications; or</li>
      <li>Use the Services in violation of applicable law.</li>
    </UL>
    <p>
      We may remove Content, reject submissions, or suspend or terminate accounts
      that violate these Terms.
    </p>

    <H2>5. Event Submissions and Moderation</H2>
    <p>
      Event submissions are reviewed by our administrators, who decide in their sole
      discretion whether to publish, edit, or reject them. We are not obligated to
      publish any submission.
    </p>

    <H2>6. Third-Party Content and Links</H2>
    <p>
      Events may include links to third-party websites, such as ticketing pages,
      venue maps, or organizer websites. We do not control and are not responsible
      for third-party sites, their content, or their transactions. Ticket purchases
      and other transactions occur solely between you and the third party. Event
      details are provided by organizers and submitters; we do not guarantee their
      accuracy, and events may be changed or cancelled by their organizers at any
      time.
    </p>

    <H2>7. Intellectual Property</H2>
    <p>
      The Services, including their software, design, and branding, are owned by us
      or our licensors and are protected by intellectual-property laws. We grant you
      a limited, non-exclusive, non-transferable, revocable license to use the
      Services, including the mobile apps on devices you own or control, for their
      intended purpose. You may not copy, modify, distribute, sell, lease, or reverse
      engineer any part of the Services except as permitted by law.
    </p>

    <H2>8. Copyright Complaints</H2>
    <p>
      If you believe Content on the Services infringes your copyright, send a notice
      to <A href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</A> identifying the
      work, the infringing material and its location, your contact information, a
      statement of good-faith belief that the use is unauthorized, and a statement,
      under penalty of perjury, that the information in your notice is accurate and
      that you are the owner or authorized to act for the owner. We will respond to
      valid notices, including removing infringing material where appropriate.
    </p>

    <H2>9. Termination</H2>
    <p>
      You may stop using the Services at any time and may request deletion of your
      account as described in the Privacy Policy. We may suspend or terminate your
      access to the Services at any time for violation of these Terms, risk to other
      users or to us, or discontinuation of the Services. Sections that by their
      nature should survive termination (including Sections 3, 6, 10, 11, and 12)
      survive.
    </p>

    <H2>10. Disclaimers</H2>
    <p>
      THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
      KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR
      A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
      SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT EVENT
      INFORMATION IS ACCURATE OR CURRENT.
    </p>

    <H2>11. Limitation of Liability</H2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT,
      INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF
      PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF THE
      SERVICES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE
      SERVICES WILL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS (US $100) OR
      THE AMOUNTS YOU PAID US FOR THE SERVICES IN THE TWELVE MONTHS BEFORE THE
      CLAIM. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THESE
      LIMITATIONS MAY NOT APPLY TO YOU.
    </p>

    <H2>12. Indemnification</H2>
    <p>
      You agree to indemnify and hold us harmless from claims, damages, and expenses
      (including reasonable attorneys' fees) arising from your Content, your use of
      the Services, or your violation of these Terms or applicable law.
    </p>

    <H2>13. Mobile App Stores</H2>
    <p>
      The mobile apps are distributed through the Apple App Store and Google Play.
      Your use of the apps is also subject to the applicable store's terms. Apple and
      Google are not parties to these Terms, have no obligation to provide support or
      maintenance for the apps, and are not responsible for addressing any claims
      relating to the apps; Apple and its subsidiaries are third-party beneficiaries
      of these Terms with respect to your use of the iOS app.
    </p>

    <H2>14. Changes to the Services and These Terms</H2>
    <p>
      We may modify or discontinue the Services at any time. We may update these
      Terms from time to time; the updated Terms will be posted on this page with a
      new effective date, and material changes will be communicated through the
      Services or by email. Continued use of the Services after changes take effect
      constitutes acceptance of the updated Terms.
    </p>

    <H2>15. Governing Law and Disputes</H2>
    <p>
      These Terms are governed by the laws of the State of Maryland, without regard
      to conflict-of-law rules. Before filing a claim, you agree to contact us and
      attempt to resolve the dispute informally. Any dispute that cannot be resolved
      informally will be brought in the state or federal courts located in Maryland,
      and you consent to their jurisdiction.
    </p>

    <H2>16. Contact</H2>
    <p>
      What's Happening Baltimore — Baltimore, Maryland —{' '}
      <A href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</A>
    </p>
  </LegalLayout>
);
